import { Avatar, Div, Inline, LinkButton, Popover, Stack, Tag, Text, VisuallyHidden } from "@hopper-ui/components";
import { AngleRightIcon } from "@hopper-ui/icons";
import type { ODataUser } from "../../api/types.ts";
import { userDetailsQueryOptions } from "../../api/routes/odata/details.ts";
import {
    type RenderUserDetailsCardProps,
    Skeleton,
    textEllipsisProps,
    useDateFormatters,
    useDaysFormatters
} from "@sg-protect/components";
import { MixpanelProperty, useTrackButtonClicked, useTrackLinkClicked, useTrackModalState } from "@sg-protect/shell";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useLayoutEffect, useRef, useState } from "react";
import { PreviewTrigger, type PreviewTriggerProps } from "react-aria-components";
import { type NavigateOptions, useParams } from "react-router";
import { useUserDetailsHref } from "../../hooks/user-details/useUserDetailsHref.ts";
import { CopyRowButton } from "../CopyRowButton.tsx";

const USER_DETAILS_CARD_NAME = "User Details Card";

export interface UserDetailsCardProps extends Omit<PreviewTriggerProps, "children"> {
    userId: string;
    displayName: string;
    location: string;
    routerOptions?: NavigateOptions;
    isExternal?: boolean;
    children: ReactNode;
}

export function UserDetailsCard({
    userId,
    displayName,
    location,
    routerOptions,
    isExternal,
    children,
    onOpenChange,
    ...previewTriggerProps
}: UserDetailsCardProps) {
    const [trackModalOpened] = useTrackModalState();

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                trackModalOpened(USER_DETAILS_CARD_NAME, undefined, { [MixpanelProperty.Location]: location });
            }

            onOpenChange?.(isOpen);
        },
        [trackModalOpened, location, onOpenChange]
    );

    return (
        <PreviewTrigger {...previewTriggerProps} onOpenChange={handleOpenChange}>
            {children}
            <UserDetailsPopover
                userId={userId}
                displayName={displayName}
                routerOptions={routerOptions}
                isExternal={isExternal}
            />
        </PreviewTrigger>
    );
}

function UserIdentityCard({
    userId,
    displayName,
    routerOptions,
    isExternal,
    children,
    fallback
}: RenderUserDetailsCardProps) {
    const { userId: currentUserId } = useParams();

    if (userId.toLowerCase() === currentUserId?.toLowerCase()) {
        return fallback;
    }

    return (
        <UserDetailsCard
            userId={userId}
            displayName={displayName}
            location="User Identity"
            routerOptions={routerOptions}
            isExternal={isExternal}
        >
            {children}
        </UserDetailsCard>
    );
}

export function renderUserIdentityCard(props: RenderUserDetailsCardProps) {
    return <UserIdentityCard {...props} />;
}

interface UserDetailsPopoverProps {
    userId: string;
    displayName: string;
    routerOptions?: NavigateOptions;
    isExternal?: boolean;
}

function UserDetailsPopover(props: UserDetailsPopoverProps) {
    return (
        // TODO: drop shouldSkipAnimation once RAC fixes it; when PreviewTrigger skips the exit animation,
        // a quickly closed preview can remount later as an unpositioned, never-ending exit at the top-left.
        <Popover
            isNonDialog
            shouldSkipAnimation={false}
            placement="top start"
            UNSAFE_width="25rem"
            aria-label="User details"
        >
            <UserDetailsCardContent {...props} />
        </Popover>
    );
}

function UserDetailsCardContent({ userId, displayName, routerOptions, isExternal }: UserDetailsPopoverProps) {
    const { data: user, isPending, isError } = useQuery(userDetailsQueryOptions(userId));
    const href = useUserDetailsHref()(userId);
    const trackLinkClicked = useTrackLinkClicked();

    if (isPending) {
        return <UserDetailsCardSkeleton />;
    }

    const viewDetailsLink = (
        <LinkButton
            href={href}
            routerOptions={routerOptions}
            isExternal={isExternal}
            variant="ghost-primary"
            size="sm"
            alignSelf="flex-end"
            marginRight="inline-md"
            marginBottom="stack-md"
            onPress={() => trackLinkClicked("View details", USER_DETAILS_CARD_NAME)}
        >
            <Text data-public>View details</Text>
            <AngleRightIcon slot="end-icon" size="sm" />
        </LinkButton>
    );

    if (isError) {
        return (
            <CardLayout footer={viewDetailsLink}>
                <UserSummary name={displayName} />
            </CardLayout>
        );
    }

    if (!user) {
        return (
            <CardLayout>
                <UserSummary name={displayName} />
            </CardLayout>
        );
    }

    const name = user.displayName ?? displayName;
    const role = [user.jobTitle, user.department].filter(Boolean).join(", ");

    return (
        <CardLayout footer={viewDetailsLink}>
            <UserSummary name={name} role={role} email={user.email} />
            <LastSignIn lastSignInDate={user.lastSignInDate} />
            <Tag variant={user.type === "guest" ? "caution" : "option1"} borderRadius="circle">
                <UserTypeLabel type={user.type} />
            </Tag>
        </CardLayout>
    );
}

interface UserSummaryProps {
    name: string;
    role?: string;
    email?: string | null;
}

function UserSummary({ name, role, email }: UserSummaryProps) {
    return (
        <Inline gap="inline-md" alignY="center" wrap={false} width="100%">
            <Avatar name={name} aria-label="User avatar" size="xl" />
            <Stack gap="stack-xs" minWidth={0} flex={1}>
                <Stack gap="core_0">
                    <Text fontWeight="body-md-semibold" wordBreak="break-word">
                        {name}
                    </Text>
                    {role && (
                        <Text size="xs" color="neutral-weak" wordBreak="break-word">
                            {role}
                        </Text>
                    )}
                </Stack>
                {email && <UserEmail email={email} />}
            </Stack>
        </Inline>
    );
}

interface UserEmailProps {
    email: string;
}

function UserEmail({ email }: UserEmailProps) {
    const trackButtonClicked = useTrackButtonClicked();
    const atIndex = email.lastIndexOf("@");
    const localPart = atIndex === -1 ? email : email.slice(0, atIndex);
    const domain = atIndex === -1 ? "" : email.slice(atIndex);
    const { containerRef, buttonRef, textRef, localPartRef, visibleLocalPart } = useFittedLocalPart(localPart, domain);

    return (
        <Div ref={containerRef} minWidth={0}>
            <CopyRowButton
                ref={buttonRef}
                value={email}
                label={<VisuallyHidden data-public>Copy email</VisuallyHidden>}
                errorMessage="Failed to copy the email to clipboard. Please try copying it manually."
                onCopy={() => trackButtonClicked("Copy email", USER_DETAILS_CARD_NAME)}
            >
                <Inline ref={textRef} gap="core_0" wrap={false} minWidth={0} aria-hidden>
                    <Text ref={localPartRef} size="xs" fontWeight="body-xs" whiteSpace="nowrap" flexShrink={0}>
                        {visibleLocalPart}
                    </Text>
                    {domain && (
                        <Text size="xs" fontWeight="body-xs" {...textEllipsisProps}>
                            {domain}
                        </Text>
                    )}
                </Inline>
            </CopyRowButton>
        </Div>
    );
}

// CSS text-overflow leaves a partial-character gap before the domain, or clips the "…" when space is tight,
// so the local part is cut to whole characters that fit what the domain and the button chrome leave.
function useFittedLocalPart(localPart: string, domain: string) {
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const textRef = useRef<HTMLDivElement>(null);
    const localPartRef = useRef<HTMLSpanElement>(null);
    const [visibleLocalPart, setVisibleLocalPart] = useState(localPart);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const button = buttonRef.current;
        const text = textRef.current;
        const localPartElement = localPartRef.current;
        const context = document.createElement("canvas").getContext("2d");

        if (!container || !button || !text || !localPartElement || !context) {
            return;
        }

        const fit = () => {
            context.font = getComputedStyle(localPartElement).font;
            const measure = (value: string) => context.measureText(value).width;
            const chromeWidth = button.getBoundingClientRect().width - text.getBoundingClientRect().width;
            // 1px of slack absorbs canvas vs. DOM sub-pixel rounding, which would otherwise ellipsize the domain.
            const available = container.clientWidth - chromeWidth - measure(domain) - 1;

            setVisibleLocalPart(fitWithEllipsis(localPart, available, measure));
        };

        fit();

        const observer = new ResizeObserver(fit);
        observer.observe(container);
        void document.fonts.ready.then(fit);

        return () => observer.disconnect();
    }, [localPart, domain]);

    return { containerRef, buttonRef, textRef, localPartRef, visibleLocalPart };
}

function fitWithEllipsis(value: string, available: number, measure: (value: string) => number) {
    if (measure(value) <= available) {
        return value;
    }

    let low = 0;
    let high = value.length - 1;

    while (low < high) {
        const middle = Math.ceil((low + high) / 2);

        if (measure(`${value.slice(0, middle)}…`) <= available) {
            low = middle;
        } else {
            high = middle - 1;
        }
    }

    return `${value.slice(0, low)}…`;
}

interface CardLayoutProps {
    footer?: ReactNode;
    children: ReactNode;
}

function CardLayout({ footer, children }: CardLayoutProps) {
    return (
        <Stack gap="core_0" minWidth={0}>
            <Stack
                gap="stack-md"
                alignX="start"
                paddingTop="inset-md"
                paddingX="inset-lg"
                paddingBottom={footer ? undefined : "inset-md"}
            >
                {children}
            </Stack>
            {footer}
        </Stack>
    );
}

interface LastSignInProps {
    lastSignInDate: ODataUser["lastSignInDate"];
}

function LastSignIn({ lastSignInDate }: LastSignInProps) {
    const { toShortDate } = useDateFormatters();
    const { formatDaysAgo } = useDaysFormatters();

    if (!lastSignInDate) {
        return (
            <Text size="xs" color="neutral-weak" data-public>
                Never signed in
            </Text>
        );
    }

    return (
        <Text size="xs" color="neutral-weak">
            Last signed in {formatDaysAgo(lastSignInDate)} ({toShortDate(lastSignInDate)})
        </Text>
    );
}

interface UserTypeLabelProps {
    type: ODataUser["type"];
}

// Each label is its own literal leaf so data-public never unmasks a value from the API.
function UserTypeLabel({ type }: UserTypeLabelProps) {
    switch (type) {
        case "member":
            return <Text data-public>Member</Text>;
        case "guest":
            return <Text data-public>Guest</Text>;
        default:
            return <Text data-public>Unknown</Text>;
    }
}

function UserDetailsCardSkeleton() {
    return (
        <Stack gap="stack-md" paddingY="inset-md" paddingX="inset-lg">
            <Inline gap="inline-md" alignY="center" wrap={false}>
                <Skeleton width="64px" height="64px" borderRadius="circle" />
                <Stack gap="stack-xs" flex={1}>
                    <Skeleton height="16px" width="70%" />
                    <Skeleton height="12px" width="50%" />
                </Stack>
            </Inline>
            <Skeleton height="12px" width="80%" />
            <Skeleton height="12px" width="60%" />
        </Stack>
    );
}
